import { useCallback, useRef } from "react";
import calculateDataSize from "../utils/calculateDataSize";
import { axiosProtected } from "../utils/axios";

export default function useServer() {
  const abortControllers = useRef(new Map());

  const createSignal = (endpoint) => {
    if (abortControllers.current.has(endpoint)) {
      abortControllers.current.get(endpoint).abort();
    }
    const controller = new AbortController();
    abortControllers.current.set(endpoint, controller);

    return controller.signal;
  };

  const cleanupSignal = (endpoint) => {
    abortControllers.current.delete(endpoint);
  };

  const get = useCallback(
    async (route, query = null) => {
      try {
        const signal = createSignal(route);
        const response = await axiosProtected.get(route, {
          params: query,
          signal: signal,
        });
        cleanupSignal(route);
        return response.data;
      } catch (error) {
        if (error.code === "ERR_CANCELED") {
          console.log("Request was cancelled");
          throw error;
        }
        throw error;
      }
    },
    []
  );

  const post = useCallback(
    async (route, data) => {
      try {
        const signal = createSignal(route);
        const response = await axiosProtected.post(
          route,
          {
            data: data,
          },
          signal
        );
        cleanupSignal(route);

        const { bytes, kilobytes, megabytes } = calculateDataSize(
          response.data
        );
        console.log(
          `${route}, Size: ${bytes} bytes | ${kilobytes} KB | ${megabytes} MB`
        );

        return response.data;
      } catch (error) {
        if (error.code === "ERR_CANCELED") {
          console.log("Request was cancelled");
          throw error;
        }
        throw error;
      }
    },
    []
  );

  const file = useCallback(
    async (route, fileData) => {
      const signal = createSignal(route);
      const response = await axiosProtected.post(route, fileData, {
        signal: signal,
      }); //doubl chedk
      cleanupSignal(route);
      return response;
    },
    []
  );

  const blob = useCallback(
    async (route, data) => {
      try {
        const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
        const blob = new Blob([JSON.stringify(data)]);
        const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);
        const fileId = Date.now().toString();

        // Create unique endpoint identifier for this blob upload
        const uploadId = `${route}-${fileId}`;
        const signal = createSignal(uploadId);

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, blob.size);
          const chunk = blob.slice(start, end);

          const formData = new FormData();
          formData.append("fileId", fileId);
          formData.append("chunkIndex", chunkIndex);
          formData.append("totalChunks", totalChunks);
          formData.append("chunk", new Blob([chunk]));

          const response = await axiosProtected.post(route, formData, {
            signal,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          // For the last chunk
          if (chunkIndex === totalChunks - 1) {
            cleanupSignal(uploadId);
            response.fileId = fileId;
            response.totalChunks = totalChunks;
            return response;
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Blob upload was cancelled");
          return null;
        }
        throw error;
      }
    },
    []
  );

  const abortAll = useCallback(() => {
    console.log("ABORTING FETCH");
    abortControllers.current.forEach((controller) => {
      controller.abort();
    });
    abortControllers.current.clear();
  }, []);

  return { get, post, file, blob, abortAll };
}
